// src/components/chat/RecordingControls.tsx
// -----------------------------------------------------------------------------
// Compact, ChatGPT-style recording controls shared by the patient chat and the
// patient-create description mic: a pulsing dot, a live amplitude bar, a timer,
// a ✕ (discard) and a ✓ (stop & transcribe). Purely presentational — the host
// owns the recorder and passes amplitude/duration + the two handlers.
// -----------------------------------------------------------------------------

import React from "react";
import {
  Box,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "../../i18n";

interface RecordingControlsProps {
  amplitude: number; // 0-1
  duration: number; // seconds
  onDiscard: () => void; // ✕ — stop without transcribing
  onStop: () => void; // ✓ — stop & transcribe
}

const fmt = (s: number) =>
  `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, "0")}`;

const RecordingControls: React.FC<RecordingControlsProps> = ({
  amplitude,
  duration,
  onDiscard,
  onStop,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          flexShrink: 0,
          backgroundColor: theme.palette.secondary.main,
          animation: "recPulse 1.5s ease-in-out infinite",
          "@keyframes recPulse": {
            "0%, 100%": { opacity: 1 },
            "50%": { opacity: 0.4 },
          },
        }}
      />
      <Box sx={{ flex: 1, minWidth: 40, maxWidth: 160 }}>
        <LinearProgress
          variant="determinate"
          value={Math.min(amplitude * 100, 100)}
          sx={{
            height: 4,
            borderRadius: 1,
            backgroundColor: alpha(theme.palette.primary.main, 0.15),
            "& .MuiLinearProgress-bar": {
              backgroundColor: theme.palette.secondary.main,
              transition: "none",
            },
          }}
        />
      </Box>
      <Typography
        variant="caption"
        sx={{
          minWidth: 30,
          flexShrink: 0,
          color: theme.palette.text.secondary,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {fmt(duration)}
      </Typography>
      <Tooltip title={t("chat.discard_recording", "Discard")}>
        <IconButton
          onClick={onDiscard}
          size="small"
          sx={{ color: theme.palette.error.main, flexShrink: 0 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t("chat.stop_and_transcribe", "Stop & transcribe")}>
        <IconButton
          onClick={onStop}
          size="small"
          sx={{ color: theme.palette.success.main, flexShrink: 0 }}
        >
          <CheckIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default RecordingControls;
